// src/components/platform/PlatformPanel.tsx
import { useAuth } from "@/contexts/AuthContext";
import { SectionCard } from "@/components/common/SectionCard";
import { usePlatformPanel } from "./usePlatformPanel";
import { PlatformPanelOrgsTab } from "./PlatformPanelOrgsTab";
import { PlatformPanelUsersTab } from "./PlatformPanelUsersTab";
import { PlatformPanelFireflyMotionSection } from "./PlatformPanelFireflyMotionSection";
import { PlatformSubscriptionCostsPanel } from "./PlatformSubscriptionCostsPanel";
import { PlatformOrgSubscriptionsPanel } from "./PlatformOrgSubscriptionsPanel";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { PlatformViewTab } from "./platformPanelTypes";

export function PlatformPanel() {
  const auth = useAuth();
  const realPlatformRpcAccess = auth.realHasRole("platform_admin", "platform_dev");
  const p = usePlatformPanel({ enabled: realPlatformRpcAccess });

  const canMutate = auth.hasRole("platform_dev", "platform_admin");
  const canManageOrgs = auth.hasRole("platform_dev", "platform_admin");
  const canSeeCosts = auth.realHasRole("platform_owner", "platform_admin", "platform_dev");
  const canManageSubscriptions = auth.realHasRole("platform_owner", "platform_admin", "platform_dev");
  const tabs: { id: PlatformViewTab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "orgs", label: "Organizations" },
    { id: "users", label: "Users" },
  ];
  if (canSeeCosts) tabs.push({ id: "costs", label: "Costs" });
  if (canManageSubscriptions) tabs.push({ id: "subscriptions", label: "Subscriptions" });

  // Container/padding doctrine \u00a7K1+K5: panel root is flat \u2014 no saturated
  // indigo plate (was `rounded-2xl border-indigo-200 bg-indigo-50 p-5`).
  // Header row + SectionCard children sit directly on the shell gradient.
  // See docs/planning/container_padding_pass.md \u00a7K.
  return (
    <div
      {...{ [FIREFLY_TARGET_ATTR]: "platform-surface" }}
      className="w-full text-left space-y-4"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Platform View</h2>
          <div className="text-sm text-gray-600 mt-1 dark:text-zinc-300">
            Acting as <span className="font-semibold">{auth.platformRole ?? "platform_operator"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void p.refreshOverview()}
            className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
            disabled={p.loading}
          >
            Refresh
          </button>
        </div>
      </header>

      <div
        {...{ [FIREFLY_TARGET_ATTR]: "platform-tabs" }}
        className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1 dark:border-zinc-700 dark:bg-zinc-900/40"
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => p.setTab(t.id)}
            className={
              "text-xs sm:text-sm px-3 py-1 rounded-full " +
              (p.tab === t.id
                ? "bg-white text-indigo-800 shadow-sm dark:bg-zinc-800 dark:text-indigo-200"
                : "text-gray-600 hover:bg-white/60 dark:text-zinc-300 dark:hover:bg-zinc-800/60")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {p.msg ? (
        <div className="p-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">{p.msg}</div>
      ) : null}

      {p.lastRpcError ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/70 dark:bg-amber-950/30 dark:text-amber-100">
          <div className="font-semibold">Setup needed</div>
          <div className="mt-1">The app could not call one or more platform RPCs. Common fixes:</div>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>
              Re-run <span className="font-mono">docs/supabase_admin_onboarding.sql</span> to create the platform functions.
            </li>
            <li>Ensure execute grants exist for authenticated users (the script includes them).</li>
            <li>
              If you see "schema cache", run <span className="font-mono">NOTIFY pgrst, 'reload schema';</span> in Supabase SQL
              editor.
            </li>
          </ul>
        </div>
      ) : null}
      {!realPlatformRpcAccess ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800/70 dark:bg-amber-950/30 dark:text-amber-100">
          Platform RPC data is disabled because your real session is missing platform RPC permissions (`platform_admin` or `platform_dev`). Impersonation can change UI permissions, but RPC access still follows the real JWT.
        </div>
      ) : null}

      {p.tab === "overview" ? (
        <>
          <SectionCard title="Overview" description="Counts of orgs, pending approvals, and blocked users across the platform.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Organizations</div>
              <div className="text-2xl font-semibold mt-1 text-gray-900 dark:text-zinc-100">{p.orgs.length}</div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Pending approvals</div>
              <div className="text-2xl font-semibold mt-1 text-gray-900 dark:text-zinc-100">{p.pendingProfiles.length}</div>
            </div>
            <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-900/60">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400">Blocked users</div>
              <div className="text-2xl font-semibold mt-1 text-gray-900 dark:text-zinc-100">{p.blockedProfiles.length}</div>
            </div>
          </div>
          </SectionCard>
          <PlatformPanelFireflyMotionSection />
        </>
      ) : null}

      {p.tab === "orgs" ? <PlatformPanelOrgsTab p={p} canManageOrgs={canManageOrgs} /> : null}
      {p.tab === "users" ? <PlatformPanelUsersTab p={p} canMutate={canMutate} /> : null}
      {p.tab === "costs" && canSeeCosts ? <PlatformSubscriptionCostsPanel /> : null}
      {p.tab === "subscriptions" && canManageSubscriptions ? <PlatformOrgSubscriptionsPanel orgs={p.orgs} /> : null}
    </div>
  );
}
